import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/components/FinalCTA";
import Seo from "@/components/Seo";
import { Icon } from "@/components/Icon";
import { VALUES, ABOUT_HIGHLIGHTS, IMAGES, COMPANY } from "@/lib/content";

const About = () => {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us"
        description="Established in 2011, Best Bizz Financial is a trusted accounting partner for Aruban businesses — built on trust, professionalism and genuinely personal service, with fixed monthly fees and QuickBooks Online."
        path="/about"
        image="/images/about.webp"
      />
      <PageHeader
        overline="About Us"
        title="Personal service and financial confidence since 2011"
        subtitle="Best Bizz Financial & Management Services N.V. helps businesses across Aruba stay organized, compliant and confident about their finances."
      />

      {/* Our Story */}
      <section className="bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-7">
              <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#4B5CAB]">
                Our Story
              </p>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-[-0.02em] text-[#2A2E7A] leading-[1.15]">
                A dependable partner for growing Aruban businesses
              </h2>
              <div className="mt-6 space-y-5 text-lg text-[#51607A] leading-relaxed">
                <p>
                  Established in {COMPANY.since}, Best Bizz Financial has built its
                  reputation on trust, professionalism and genuinely personal
                  service. We work closely with each client to keep their books
                  accurate, their payroll reliable and their financial decisions
                  well informed.
                </p>
                <p>
                  Using QuickBooks Online, we give our clients secure, around-the-clock
                  access to their financial information, paired with clear reporting and
                  fixed monthly fees. The result is simple: dependable numbers, no
                  surprises, and more time to focus on running the business.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ABOUT_HIGHLIGHTS.map((h) => (
                  <div key={h.label} className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#F5F6FB]">
                      <Icon name={h.icon} className="h-5 w-5 text-[#4B5CAB]" />
                    </span>
                    <span className="text-base font-medium text-[#2A2E7A]">{h.label}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden shadow-[0_20px_50px_-24px_rgba(36,59,99,0.3)] border border-[#2A2E7A]/5">
                <img
                  src={IMAGES.about}
                  alt="Accountant reviewing financial growth on a calculator"
                  className="w-full h-[440px] lg:h-[520px] object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F5F6FB]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <Reveal className="max-w-2xl">
            <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#4B5CAB]">
              What we stand for
            </p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-[-0.02em] text-[#2A2E7A] leading-[1.15]">
              The values behind every client relationship
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div
                  data-testid={`value-card-${v.title.toLowerCase()}`}
                  className="h-full bg-white p-8 rounded-lg shadow-sm border border-[#2A2E7A]/8"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-md bg-[#2A2E7A]">
                    <Icon name={v.icon} className="h-7 w-7 text-white" />
                  </span>
                  <h3 className="mt-5 font-heading text-2xl font-semibold text-[#2A2E7A]">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-base text-[#51607A] leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default About;
